import { useState } from "react";
import { FileText, Download, Trash2, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn, formatDateDDMMYYYY } from "@/lib/utils";
import type { TransactionDocument } from "@/types/transaction-document";

interface DocumentListItemProps {
  document: TransactionDocument;
  isSelected: boolean;
  onSelectChange: (id: string, checked: boolean) => void;
  onPreview: (document: TransactionDocument) => void;
  onDownload: (document: TransactionDocument) => void;
  onDelete: (document: TransactionDocument) => void;
  isDeleting?: boolean;
}

/** Maps MIME types to a short display label shown in the icon badge */
function getFileIconLabel(fileType: string): string {
  if (fileType.includes("pdf")) return "PDF";
  if (fileType.includes("word")) return "DOC";
  if (fileType.includes("excel") || fileType.includes("spreadsheet")) return "XLS";
  if (fileType.includes("image")) return "IMG";
  return "FILE";
}

/** Colour for the document type label — matches the teal used in the reference screenshot */
function getDocumentTypeColour(_documentType: string) {
  return "text-[#007B7F]";
}

export function DocumentListItem({
  document,
  isSelected,
  onSelectChange,
  onPreview,
  onDownload,
  onDelete,
  isDeleting = false,
}: DocumentListItemProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const iconLabel = getFileIconLabel(document.fileType);

  return (
    <>
      <div
        className={cn(
          "flex items-start gap-3 rounded-lg border bg-card p-3 transition-colors",
          isSelected && "border-primary/40 bg-primary/5"
        )}
      >
        {/* Checkbox */}
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) =>
            onSelectChange(document.id, checked === true)
          }
          className="mt-1 flex-shrink-0"
          aria-label={`Select ${document.fileName}`}
        />

        {/* File icon */}
        <div className="flex-shrink-0 mt-0.5">
          <div className="relative flex h-9 w-9 items-center justify-center rounded-md bg-[#007B7F]/10">
            <FileText className="h-5 w-5 text-[#007B7F]" />
            <span className="absolute -bottom-1 -right-1 rounded bg-[#007B7F] px-0.5 text-[7px] font-bold leading-tight text-white">
              {iconLabel}
            </span>
          </div>
        </div>

        {/* Document info */}
        <div className="flex-1 min-w-0">
          <button
            onClick={() => onPreview(document)}
            className="text-sm font-semibold text-foreground hover:underline text-left truncate block max-w-full"
            title={document.fileName}
          >
            {document.fileName}
          </button>
          <p className="text-xs text-muted-foreground mt-0.5">
            Uploaded {formatDateDDMMYYYY(document.uploadedAt)} by{" "}
            {document.uploadedByName ?? document.uploadedByEmail}
          </p>
          <p className={cn("text-xs font-medium mt-0.5", getDocumentTypeColour(document.documentType))}>
            Document Type: {document.documentType}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={() => onDownload(document)}
            title="Download"
            aria-label={`Download ${document.fileName}`}
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting}
            title="Delete"
            aria-label={`Delete ${document.fileName}`}
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-semibold">{document.fileName}</span>? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowDeleteConfirm(false);
                onDelete(document);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
